/**
 * Cookie / analytics consent banner.
 *
 * Shows a one-time prompt on first visit asking the user to allow Google
 * Analytics. Required by GDPR/UK PECR for EU/UK visitors and aligns with
 * Australian Privacy Principle 5 (notification at point of collection).
 *
 * The default-deny consent state is set in index.html before gtag config,
 * so analytics is gated regardless of whether this component has mounted.
 */

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'hipponics_analytics_consent';

// `window.gtag` is declared in src/utils/analytics.ts with a stricter signature
// for `'config' | 'event' | 'js' | 'set'` commands. The Consent Mode v2 API
// (`gtag('consent', 'update', { ... })`) doesn't fit that signature, so we
// cast at the call site instead of redeclaring the global type.

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const choice = localStorage.getItem(STORAGE_KEY);
      if (choice === null) {
        setVisible(true);
      }
    } catch {
      // localStorage blocked — show the banner so the user can interact at least once
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const accept = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'granted');
    } catch {/* ignore */}
    if (typeof window.gtag === 'function') {
      // Cast is safe — gtag accepts ('consent', 'update', {...}) at runtime,
      // but our existing TS declaration only types the standard commands.
      (window.gtag as (...args: unknown[]) => void)('consent', 'update', {
        analytics_storage: 'granted',
      });
    }
    setVisible(false);
  };

  const decline = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'denied');
    } catch {/* ignore */}
    // No gtag update needed — defaults are already denied.
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        // Below standard modals (10000) so the first-run disclaimer takes
        // precedence, but above the map/sheet chrome. --z-banner tier.
        zIndex: 9999,
        background: '#1f2937',
        borderTop: '1px solid #374151',
        color: '#d1d5db',
        padding: '14px 16px',
        // Clear the iOS home-indicator / gesture zone so the Accept/Decline
        // buttons aren't half-swallowed by the system UI on notched phones.
        paddingBottom: 'max(14px, env(safe-area-inset-bottom, 0px))',
        boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.4)',
        fontSize: '0.85rem',
        lineHeight: 1.5,
      }}
    >
      <div
        style={{
          maxWidth: '960px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        <div>
          We use Google Analytics to understand how Firewatch is used so we can
          improve it. This sets a small cookie. You can decline and the app will
          still work normally.{' '}
          <a
            href="/privacy.html"
            style={{ color: '#f97316', textDecoration: 'underline' }}
          >
            Privacy Policy
          </a>
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={decline}
            style={{
              background: 'transparent',
              border: '1px solid #4b5563',
              color: '#d1d5db',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem',
              minHeight: '44px',
            }}
          >
            Decline
          </button>
          <button
            type="button"
            onClick={accept}
            style={{
              background: '#f97316',
              border: 'none',
              color: '#fff',
              padding: '8px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
              minHeight: '44px',
            }}
          >
            Accept analytics
          </button>
        </div>
      </div>
    </div>
  );
}
